"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  ATTIC_MAX,
  attic,
  BABYLONIAN_MAX,
  type BabylonianZero,
  babylonian,
  EGYPTIAN_MAX,
  egyptian,
  IONIC_MAX,
  ionic,
  MAYA_MAX,
  type MayaMode,
  maya,
} from "@/lib/tool-kit/numbers-languages/ancient";

/** English strings. A translated content file overrides these through `ui`. */
export const ANCIENT_NUMBER_SYSTEMS_CONVERTER_STRINGS = {
  numberLabel: "Number",
  numberHint: "A whole number from 1. Each system has its own upper limit.",
  numberPlaceholder: "2026",
  zeroLabel: "Babylonian empty place",
  zeroGap: "Leave a gap (early scribes)",
  zeroPlaceholder: "Mark it ∅ (later placeholder)",
  mayaLabel: "Maya place values",
  mayaLongCount: "Long Count calendar (1, 20, 360, 7200 …)",
  mayaBase20: "Pure base 20 (1, 20, 400, 8000 …)",
  resultHeading: "Your number in five ancient systems",
  fontHint:
    "If a system shows empty boxes, your device has no font for it; the plain-text forms below still work.",
  copy: "Copy",
  copied: "Copied!",
  enterPrompt: "Type a number to write it in ancient numerals.",
  errorInvalid: "That is not a whole number. Use digits only.",
  errorZero:
    "These systems start at 1. The Babylonians had no zero, and the Egyptian and Greek numerals have no sign for it.",
  outOfRange: "This system is shown for {min} to {max}.",
  babylonianTitle: "Babylonian cuneiform (base 60)",
  babylonianDigits: "Base-60 digits: {digits}",
  babylonianPlain: "Typed form (Y = 1, < = 10): {plain}",
  babylonianDropped:
    "Trailing empty places are not written, as in the tablets, so this looks the same as a smaller number. Context told the reader the scale.",
  egyptianTitle: "Egyptian hieroglyphs (base 10, additive)",
  atticTitle: "Attic Greek (acrophonic, additive)",
  atticFallback: "Plain letters: {text}",
  ionicTitle: "Ionic Greek (alphabetic)",
  mayaTitle: "Maya (base 20, positional)",
  mayaDotted: "Long Count: {dotted}",
  mayaCalendarNote:
    "Counts days from the start of the Long Count; a date also needs a correlation constant to reach the Gregorian calendar.",
  colSign: "Sign",
  colCode: "Code",
  colValue: "Value",
  colTimes: "Times",
  colPlain: "Plain letters",
  colLetter: "Letter",
  colPlace: "Place",
  colWorth: "Worth",
  colDigit: "Digit",
  colGlyph: "Glyph",
  notePositional:
    "Positional: a digit's place gives its worth, so zero is needed (or at least a gap).",
  noteAdditive:
    "Additive: the signs are simply added up, in any order. No zero and no place value.",
} as const;

type Strings = typeof ANCIENT_NUMBER_SYSTEMS_CONVERTER_STRINGS;

function Card({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <section className={`${RESULT_CARD} space-y-3`}>
      <h3 className={WIDGET_H3}>{title}</h3>
      {children}
      {note ? <p className="text-foreground text-sm">{note}</p> : null}
    </section>
  );
}

function Range({
  max,
  strings,
  fmt,
}: {
  max: number;
  strings: Strings;
  fmt: (n: number) => string;
}) {
  return (
    <p className="text-foreground text-sm">
      {t(strings.outOfRange, { min: fmt(1), max: fmt(max) })}
    </p>
  );
}

function Glyphs({
  text,
  lang,
  copy,
  copied,
}: {
  text: string;
  lang?: string;
  copy: string;
  copied: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <p
        className="break-words text-3xl text-foreground leading-relaxed"
        lang={lang}
      >
        {text}
      </p>
      <CopyButton copiedLabel={copied} label={copy} text={text} />
    </div>
  );
}

interface Props {
  n: number;
  zero: BabylonianZero;
  mode: MayaMode;
  strings: Strings;
}

function BabylonianCard({ n, zero, strings }: Omit<Props, "mode">) {
  const fmt = useToolNumber();
  const result = babylonian(n, zero);
  return (
    <Card note={strings.notePositional} title={strings.babylonianTitle}>
      {result === null ? (
        <Range fmt={fmt} max={BABYLONIAN_MAX} strings={strings} />
      ) : (
        <>
          <Glyphs
            copied={strings.copied}
            copy={strings.copy}
            lang="akk"
            text={result.text}
          />
          <p className="text-foreground text-sm">
            {t(strings.babylonianDigits, { digits: result.digits.join(" : ") })}
          </p>
          <p className="font-mono text-foreground text-sm">
            {t(strings.babylonianPlain, { plain: result.plain })}
          </p>
          {result.droppedZeros > 0 ? (
            <p className="text-foreground text-sm">
              {strings.babylonianDropped}
            </p>
          ) : null}
        </>
      )}
    </Card>
  );
}

function EgyptianCard({ n, strings }: Pick<Props, "n" | "strings">) {
  const fmt = useToolNumber();
  const result = egyptian(n);
  return (
    <Card note={strings.noteAdditive} title={strings.egyptianTitle}>
      {result === null ? (
        <Range fmt={fmt} max={EGYPTIAN_MAX} strings={strings} />
      ) : (
        <>
          <Glyphs
            copied={strings.copied}
            copy={strings.copy}
            lang="egy"
            text={result.text}
          />
          <DataTable
            head={[
              strings.colSign,
              strings.colCode,
              strings.colValue,
              strings.colTimes,
            ]}
            rows={result.rows.map((r) => [
              r.sign.glyph,
              r.sign.code,
              fmt(r.sign.value),
              r.count,
            ])}
          />
        </>
      )}
    </Card>
  );
}

function MayaCard({ n, mode, strings }: Omit<Props, "zero">) {
  const fmt = useToolNumber();
  const result = maya(n, mode);
  return (
    <Card note={strings.notePositional} title={strings.mayaTitle}>
      {result === null ? (
        <Range fmt={fmt} max={MAYA_MAX} strings={strings} />
      ) : (
        <>
          <DataTable
            head={[
              strings.colPlace,
              strings.colWorth,
              strings.colDigit,
              strings.colGlyph,
            ]}
            rows={result.places.map((p) => [
              p.name,
              fmt(p.value),
              p.digit,
              p.glyph,
            ])}
          />
          {mode === "longCount" ? (
            <p className="font-mono text-foreground text-sm">
              {t(strings.mayaDotted, { dotted: result.dotted })}
            </p>
          ) : null}
          {mode === "longCount" ? (
            <p className="text-foreground text-sm">
              {strings.mayaCalendarNote}
            </p>
          ) : null}
        </>
      )}
    </Card>
  );
}

function AtticCard({ n, strings }: Pick<Props, "n" | "strings">) {
  const fmt = useToolNumber();
  const result = attic(n);
  return (
    <Card note={strings.noteAdditive} title={strings.atticTitle}>
      {result === null ? (
        <Range fmt={fmt} max={ATTIC_MAX} strings={strings} />
      ) : (
        <>
          <Glyphs
            copied={strings.copied}
            copy={strings.copy}
            lang="grc"
            text={result.text}
          />
          <p className="text-foreground text-sm" lang="grc">
            {t(strings.atticFallback, { text: result.fallback })}
          </p>
          <DataTable
            head={[
              strings.colSign,
              strings.colPlain,
              strings.colValue,
              strings.colTimes,
            ]}
            rows={result.rows.map((r) => [
              r.sign.glyph,
              r.sign.fallback,
              fmt(r.sign.value),
              r.count,
            ])}
          />
        </>
      )}
    </Card>
  );
}

function IonicCard({ n, strings }: Pick<Props, "n" | "strings">) {
  const fmt = useToolNumber();
  const result = ionic(n);
  return (
    <Card note={strings.noteAdditive} title={strings.ionicTitle}>
      {result === null ? (
        <Range fmt={fmt} max={IONIC_MAX} strings={strings} />
      ) : (
        <>
          <Glyphs
            copied={strings.copied}
            copy={strings.copy}
            lang="grc"
            text={result.text}
          />
          <DataTable
            head={[strings.colLetter, strings.colValue]}
            rows={result.letters.map((l) => [l.letter, fmt(l.value)])}
          />
        </>
      )}
    </Card>
  );
}

export default function AncientNumberSystemsConverterWidget() {
  const strings = useToolUi(ANCIENT_NUMBER_SYSTEMS_CONVERTER_STRINGS);
  const [raw, setRaw] = useState("2026");
  const [zero, setZero] = useState<BabylonianZero>("gap");
  const [mode, setMode] = useState<MayaMode>("longCount");
  const parsed = parseIntegerInput(raw);
  let message: string | null = null;
  if (raw.trim() !== "") {
    if (parsed === null || raw.trim().startsWith("-")) {
      message = parsed === null ? strings.errorInvalid : strings.errorZero;
    } else if (parsed < 1) {
      message = strings.errorZero;
    }
  }
  const n = message === null && parsed !== null ? parsed : null;

  return (
    <div className="space-y-6">
      <TextField
        hint={strings.numberHint}
        id="ans-number"
        label={strings.numberLabel}
        numeric
        onChange={setRaw}
        placeholder={strings.numberPlaceholder}
        value={raw}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.zeroLabel}
          name="ans-zero"
          onChange={setZero}
          options={[
            { value: "gap", label: strings.zeroGap },
            { value: "placeholder", label: strings.zeroPlaceholder },
          ]}
          value={zero}
        />
        <PillGroup
          label={strings.mayaLabel}
          name="ans-maya"
          onChange={setMode}
          options={[
            { value: "longCount", label: strings.mayaLongCount },
            { value: "base20", label: strings.mayaBase20 },
          ]}
          value={mode}
        />
      </div>
      {message !== null ? <ErrorNotice>{message}</ErrorNotice> : null}
      {raw.trim() === "" ? <Notice>{strings.enterPrompt}</Notice> : null}
      {n === null ? null : (
        <div aria-live="polite" className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.resultHeading}</h2>
          <p className="text-foreground text-sm">{strings.fontHint}</p>
          <BabylonianCard n={n} strings={strings} zero={zero} />
          <EgyptianCard n={n} strings={strings} />
          <MayaCard mode={mode} n={n} strings={strings} />
          <AtticCard n={n} strings={strings} />
          <IonicCard n={n} strings={strings} />
        </div>
      )}
    </div>
  );
}
